import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Layout, Package, Settings, Search,
  LogOut, Menu, X, ChevronRight, ShoppingCart, Users, Landmark, Telescope, Bot, Library, Truck, Key, HelpCircle
} from 'lucide-react';
import { useState } from 'react';
import { useLang } from '../context/LangContext';
import { useAuth, ROLE_LABELS, ROLE_COLORS } from '../context/AuthContext';
import type { UserRole } from '../context/AuthContext';

// Structura nav cu secțiunea de acces
const ALL_NAV: { path: string; section: string; icon: React.ElementType; ro: string; en: string; exact?: boolean }[] = [
  { path: '/',          section: 'dashboard', icon: LayoutDashboard, ro: 'Dashboard',       en: 'Dashboard',     exact: true },
  { path: '/blog',      section: 'blog',      icon: FileText,        ro: 'Blog',             en: 'Blog' },
  { path: '/media',     section: 'media',     icon: Library,         ro: 'Librărie Media',   en: 'Media Library' },
  { path: '/pages',     section: 'pages',     icon: Layout,          ro: 'Pagini',           en: 'Pages' },
  { path: '/products',  section: 'products',  icon: Package,         ro: 'Produse',          en: 'Products' },
  { path: '/orders',    section: 'orders',    icon: ShoppingCart,    ro: 'Comenzi',          en: 'Orders' },
  { path: '/clients',   section: 'clients',   icon: Users,           ro: 'Clienți',          en: 'Clients' },
  { path: '/prospects', section: 'prospects', icon: Telescope,       ro: 'Prospecți',        en: 'Prospects' },
  { path: '/financial', section: 'financial', icon: Landmark,        ro: 'Financiar',        en: 'Financial' },
  { path: '/seo',       section: 'seo',       icon: Search,          ro: 'SEO',              en: 'SEO' },
  { path: '/chatbot',   section: 'chatbot',   icon: Bot,             ro: 'Chat Bot AI',      en: 'Chat Bot AI' },
  { path: '/curierat',  section: 'curierat',  icon: Truck,           ro: 'Curierat',         en: 'Shipping' },
  { path: '/settings',  section: 'settings',  icon: Settings,        ro: 'Setări Web Site',  en: 'Settings' },
  { path: '/users',     section: 'users',     icon: Users,           ro: 'Utilizatori',      en: 'Users' },
  { path: '/help',      section: 'help',      icon: HelpCircle,      ro: 'Help',              en: 'Help' },
];

const ROLE_BADGE_CLASS: Record<UserRole, string> = {
  management: 'text-purple-400 bg-purple-400/15 border-purple-400/25',
  vanzari: 'text-green-400 bg-green-400/15 border-green-400/25',
  marketing: 'text-yellow-400 bg-yellow-400/15 border-yellow-400/25',
};

export default function AdminLayout({ onLogout }: { onLogout: () => void }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const { lang, setLang, t } = useLang();
  const { currentUser, hasAccess } = useAuth();

  const visibleNav = ALL_NAV.filter(item => hasAccess(item.section));

  const handleLogout = () => { onLogout(); navigate('/login'); };

  const userInitials = currentUser
    ? currentUser.displayName.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()
    : 'A';

  return (
    <div className="flex h-screen overflow-hidden bg-[#0a0c12] text-white font-sans">
      {/* Sidebar */}
      <aside className={`flex flex-col bg-[#07090f] border-r border-white/10 transition-all duration-300 z-20 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-3 h-16 border-b border-white/10 flex-shrink-0 overflow-hidden">
          {sidebarOpen ? (
            <>
              <img src="/orb.webp" alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                style={{ filter: 'drop-shadow(0 0 5px rgba(74,184,255,0.3))' }} />
              <img src="/logo.png" alt="INTERPERSONAL"
                className="h-5 w-auto object-contain flex-shrink-0"
                style={{ filter: 'brightness(1.1) drop-shadow(0 0 4px rgba(74,184,255,0.2))' }} />
            </>
          ) : (
            <img src="/orb.webp" alt="I" className="h-8 w-8 rounded-full object-cover flex-shrink-0"
              style={{ filter: 'drop-shadow(0 0 5px rgba(74,184,255,0.3))' }} />
          )}
        </div>

        {/* User info card in sidebar */}
        {sidebarOpen && currentUser && (
          <div className="mx-2 mt-3 mb-1 px-3 py-2.5 rounded-xl border border-white/8" style={{ background: 'rgba(41,92,168,0.07)' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#295CA8,#3D72B8)' }}>
                {userInitials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-white truncate">{currentUser.displayName}</div>
                <div className={`text-[10px] px-1.5 py-0.5 rounded border inline-block mt-0.5 font-medium ${ROLE_BADGE_CLASS[currentUser.role]}`}>
                  {ROLE_LABELS[currentUser.role]}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {visibleNav.map(({ path, icon: Icon, ro, en, exact }) => (
            <NavLink
              key={path} to={path} end={exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all group
                ${isActive ? 'bg-[#295CA8] text-white font-medium' : 'text-white/60 hover:text-white hover:bg-white/5'}`
              }
              title={!sidebarOpen ? (lang === 'ro' ? ro : en) : undefined}
            >
              <Icon size={18} className="flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{lang === 'ro' ? ro : en}</span>}
              {sidebarOpen && <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-40 transition-opacity" />}
            </NavLink>
          ))}
        </nav>

        {/* Bottom: schimbare parola + logout */}
        <div className="border-t border-white/10 p-2 space-y-0.5 flex-shrink-0">
          {sidebarOpen && currentUser && (
            <button onClick={() => navigate('/users')}
              className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm text-white/40 hover:text-[#4ab8ff] hover:bg-[#4ab8ff]/5 transition-all"
              title="Cont">
              <Key size={16} className="flex-shrink-0" />
              <span className="text-xs">Schimbă Parola</span>
            </button>
          )}
          <button onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-all"
            title={!sidebarOpen ? t('Deconectare', 'Logout') : undefined}>
            <LogOut size={18} className="flex-shrink-0" />
            {sidebarOpen && <span>{t('Deconectare', 'Logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-white/10 bg-[#07090f]/80 backdrop-blur flex items-center px-4 gap-4 flex-shrink-0">
          <button onClick={() => setSidebarOpen(s => !s)}
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-all">
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>

          <div className="flex-1" />

          {/* Language selector */}
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-1">
            <button onClick={() => setLang('ro')}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition-all ${lang === 'ro' ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white'}`}>
              RO
            </button>
            <button onClick={() => setLang('en')}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition-all ${lang === 'en' ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white'}`}>
              EN
            </button>
          </div>

          {/* User chip */}
          {currentUser && (
            <div className="relative">
              <button onClick={() => setShowUserMenu(s => !s)}
                className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl border border-white/10 hover:border-white/20 transition-all"
                style={{ background: 'rgba(255,255,255,0.03)' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: 'linear-gradient(135deg,#295CA8,#3D72B8)' }}>
                  {userInitials}
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-xs font-medium text-white leading-tight">{currentUser.displayName}</div>
                  <div className={`text-[9px] font-semibold leading-tight ${ROLE_COLORS[currentUser.role].split(' ')[0]}`}>{ROLE_LABELS[currentUser.role]}</div>
                </div>
                <ChevronRight size={12} className="text-white/30 rotate-90"/>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-white/10 z-50 overflow-hidden"
                  style={{ background: '#07090f', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}>
                  <div className="px-4 py-3 border-b border-white/5">
                    <div className="text-xs font-medium text-white">{currentUser.displayName}</div>
                    <div className="text-[10px] text-white/40">@{currentUser.username}</div>
                  </div>
                  {hasAccess('users') && (
                    <button onClick={() => { navigate('/users'); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-white/60 hover:text-white hover:bg-white/5 transition-all">
                      <Users size={13}/> Gestionare Utilizatori
                    </button>
                  )}
                  <button onClick={() => { navigate('/users'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-white/60 hover:text-white hover:bg-white/5 transition-all">
                    <Key size={13}/> Schimbă Parola
                  </button>
                  <div className="border-t border-white/5 mt-1 pt-1 pb-1">
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-red-400 hover:bg-red-500/10 transition-all">
                      <LogOut size={13}/> Deconectare
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6" onClick={() => showUserMenu && setShowUserMenu(false)}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
